import {
  FOR_YOU_INFINITE_SCROLL_CONTAINER,
  RESOURCE_FILTER_OPTIONS,
  RESOURCE_SORT_BY_OPTIONS,
} from "@modules/for-you/constants";
import { FC, useEffect, useRef } from "react";
import ResourceListItem from "../ResourceListItem";
import CalculatorListItem from "../CalculatorListItem";
import StickyHeader from "react-sticky-box";
import InfiniteScroll from "react-infinite-scroll-component";
import LoadingIndicator from "@common/components/LoadingIndicator";
import Select from "@common/components/Select";
import {
  RESOURCE_TOPIC_LABEL_NAMES,
  ResourceFilterOption,
  ResourceSortOption,
  CALCULATOR_NAMES,
} from "@modules/for-you/types";
import { useArticleFilterControls } from "@modules/for-you/hooks";
import FilterOptionLabel from "../FilterOptionLabel";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi";
import { Typography, Tooltip } from "@earned/core";
import { themeTextColors } from "@common/constants";

const ResourcesList: FC = () => {
  const {
    selectedFilter,
    selectedSortBy,
    isPristine,
    isFetched,
    isLoading,
    hasNextPage,
    articles,
    handleFilter,
    handleSort,
    fetchNextPage,
  } = useArticleFilterControls();

  const scrollerRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLHeadingElement>({} as HTMLHeadingElement);

  const scrollHeaderIntoView = () => {
    if (!(scrollerRef.current && headerRef.current)) return;
    const scrollDiv = scrollerRef.current;
    const header = headerRef.current;
    const headerCoords = header.getBoundingClientRect();
    const scrollCoords = scrollDiv.getBoundingClientRect();
    const distance =
      headerCoords.top +
      header.offsetHeight +
      scrollDiv.scrollTop -
      headerCoords.height -
      scrollCoords.top -
      // account for grid section padding:
      22;
    scrollDiv.scroll({ top: distance, behavior: "smooth" });
  };

  useEffect(() => {
    if (!scrollerRef.current) {
      scrollerRef.current = document.getElementById(
        FOR_YOU_INFINITE_SCROLL_CONTAINER
      );
    }
  });

  useEffect(() => {
    if (isFetched && !isPristine) {
      scrollHeaderIntoView();
    }
  }, [isFetched, selectedFilter, selectedSortBy, isPristine]);

  return (
    <div className="min-h-[1000px]">
      <div className="flex flex-row items-center gap-2 pb-[2px]">
        <Typography
          ref={headerRef}
          variant="body-xl"
          weight="medium"
          color="primary"
        >
          Resources
        </Typography>
        <Typography variant="body-xl" color="primary">
          For You
        </Typography>
        <div className="flex">
          <Tooltip
            title="How is this personalized?"
            description="Resources are personalized based on your unique profile."
          >
            <HiOutlineQuestionMarkCircle
              size={24}
              color={themeTextColors.secondary}
            />
          </Tooltip>
        </div>
      </div>
      <StickyHeader>
        <header className="flex flex-wrap border-b border-primary bg-green-40 py-0.5">
          <div>
            <Select
              className="h-7 !bg-green-40 pl-0 pr-9"
              buttonClassName="!p-0 h-7 border-none !bg-green-40"
              options={RESOURCE_FILTER_OPTIONS}
              value={selectedFilter}
              onChange={handleFilter}
              renderOption={(selected: ResourceFilterOption) => (
                <FilterOptionLabel label={selected.label} />
              )}
            />
          </div>
          <div>
            <Select
              className="h-7 !bg-green-40"
              buttonClassName="!p-0 h-7 border-none !bg-green-40"
              options={RESOURCE_SORT_BY_OPTIONS}
              value={selectedSortBy}
              onChange={handleSort}
              renderOption={(selected: ResourceSortOption) => (
                <span className="whitespace-nowrap">{selected.label}</span>
              )}
            />
          </div>
        </header>
      </StickyHeader>
      <InfiniteScroll
        scrollableTarget={FOR_YOU_INFINITE_SCROLL_CONTAINER}
        style={{ overflow: "hidden" }}
        scrollThreshold="10px"
        loader={<LoadingIndicator isVisible className="mt-3.5" />}
        next={() => {
          if (hasNextPage) fetchNextPage();
        }}
        dataLength={articles.length}
        hasMore={hasNextPage}
      >
        <div className="grid auto-rows-auto grid-cols-[repeat(auto-fit,_minmax(210px,_1fr))] gap-5 py-6">
          {isLoading ? (
            <LoadingIndicator
              className="lg:col-span-2 xl:col-span-3"
              isVisible
            />
          ) : (
            <>
              {[
                RESOURCE_TOPIC_LABEL_NAMES.ALL_FINANCIAL_TOPICS,
                RESOURCE_TOPIC_LABEL_NAMES.INVESTMENT_MANAGEMENT,
              ].includes(selectedFilter.label) && (
                <CalculatorListItem name={CALCULATOR_NAMES.RETIREMENT} />
              )}
              {[
                RESOURCE_TOPIC_LABEL_NAMES.ALL_FINANCIAL_TOPICS,
                RESOURCE_TOPIC_LABEL_NAMES.INSURANCE_PLANNING,
              ].includes(selectedFilter.label) && (
                <CalculatorListItem name={CALCULATOR_NAMES.LIFE_INSURANCE} />
              )}
              {[
                RESOURCE_TOPIC_LABEL_NAMES.ALL_FINANCIAL_TOPICS,
                RESOURCE_TOPIC_LABEL_NAMES.DEBT_MANAGEMENT,
              ].includes(selectedFilter.label) && (
                <CalculatorListItem name={CALCULATOR_NAMES.STUDENT_LOAN} />
              )}
              {articles.map((article, i) => (
                <ResourceListItem
                  key={article.ID}
                  article={article}
                  showIcon={
                    selectedFilter.label ===
                    RESOURCE_TOPIC_LABEL_NAMES.ALL_FINANCIAL_TOPICS
                  }
                />
              ))}
            </>
          )}
        </div>
      </InfiniteScroll>
    </div>
  );
};

export default ResourcesList;
